import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Home, Gem, Star } from "lucide-react";
import { getChapter } from "../data/story";
import { useProfile } from "../context/ProfileContext";
import { sfx } from "../services/soundfx";
import { speak } from "../services/phonicsAudio";

export default function CaveMapPage() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { current, loading } = useProfile();
  const chapter = getChapter(chapterId);

  useEffect(() => {
    if (!loading && !current) navigate("/profiles");
  }, [current, loading, navigate]);

  if (!current || !chapter || !chapter.caves) return null;
  const caves = current.caves || {};

  const caveDone = (caveId) => !!caves[`${chapter.id}-${caveId}`]?.completed;
  const caveStars = (caveId) => caves[`${chapter.id}-${caveId}`]?.stars || 0;
  const caveUnlocked = (idx) => idx === 0 || caveDone(chapter.caves[idx - 1].id);

  const open = (cave, idx) => {
    if (!caveUnlocked(idx)) {
      sfx.wrong();
      speak("Finish the cave before this one!");
      return;
    }
    sfx.unlock(); sfx.tap();
    speak(cave.title);
    setTimeout(() => navigate(`/chapter/${chapter.id}/cave/${cave.id}`), 350);
  };

  return (
    <div className="min-h-screen pb-20" style={{ background: "linear-gradient(180deg,#0F172A 0%,#1E293B 50%,#334155 100%)" }}>
      <div className="sticky top-0 z-40 flex items-center justify-between px-3 py-3 border-b-4 border-violet-400 shadow-md" style={{ background: "#0F172AF2" }}>
        <button data-testid="cavemap-home-btn" onClick={() => { sfx.tap(); navigate("/map"); }} className="pop-btn flex items-center justify-center rounded-2xl bg-white/90 border-4 border-violet-300 shadow-[0_4px_0_#A78BFA] active:shadow-none" style={{ width: 52, height: 52 }}>
          <Home className="w-6 h-6 text-slate-700" strokeWidth={2.5} />
        </button>
        <h1 className="font-display text-lg sm:text-xl font-black text-violet-200">{chapter.title}</h1>
        <div className="flex items-center gap-1 rounded-2xl bg-white/90 border-4 border-cyan-200 px-2.5 py-2 font-display font-bold text-cyan-600" data-testid="crystal-counter">
          <Gem className="w-5 h-5" /> {(current.crystals || []).filter((c) => c.type === "crystal").length}
        </div>
      </div>

      <p className="text-center text-indigo-200 font-bold text-sm pt-4 px-6">Win an award in every cave — clear all 10 to earn the Sound Crystal! ✨</p>

      <div className="relative max-w-md mx-auto px-6 pt-6">
        {chapter.caves.map((cave, idx) => {
          const unlocked = caveUnlocked(idx);
          const done = caveDone(cave.id);
          const stars = caveStars(cave.id);
          const side = idx % 2 === 0 ? "justify-start" : "justify-end";
          const isCurrent = unlocked && !done;
          return (
            <div key={cave.id} className="relative">
              {idx > 0 && <div className={`absolute -top-8 ${idx % 2 === 0 ? "right-1/4" : "left-1/4"} w-1.5 h-10 path-dots rounded-full opacity-50`} />}
              <div className={`flex ${side} mb-11`}>
                <motion.button
                  data-testid={`cave-node-${cave.id}`}
                  onClick={() => open(cave, idx)}
                  whileTap={{ scale: 0.92 }}
                  animate={isCurrent ? { y: [0, -7, 0] } : {}}
                  transition={isCurrent ? { repeat: Infinity, duration: 1.6 } : {}}
                  className="relative flex flex-col items-center"
                >
                  <div className={`relative flex items-center justify-center rounded-full border-[5px] border-white ${isCurrent ? "animate-pulse-glow" : ""}`}
                    style={{ width: 96, height: 96, background: unlocked ? (cave.boss ? "#DC2626" : "#7C3AED") : "#475569", boxShadow: "0 6px 0 rgba(0,0,0,0.4)", filter: unlocked ? "none" : "grayscale(0.6)" }}>
                    <span className="text-4xl">{unlocked ? (cave.boss ? "🐉" : "🕳️") : ""}</span>
                    {!unlocked && <Lock className="absolute w-8 h-8 text-white" strokeWidth={2.5} />}
                    {done && <div className="absolute -top-2 -right-2 text-xl">✅</div>}
                  </div>
                  <div className="flex gap-0.5 mt-1.5">
                    {[1, 2, 3].map((n) => (
                      <Star key={n} className="w-4 h-4" strokeWidth={2} style={{ fill: n <= stars ? "#FACC15" : "transparent", color: n <= stars ? "#F59E0B" : "#64748B" }} />
                    ))}
                  </div>
                  <span className="font-display font-bold text-slate-100 text-sm text-center">{cave.title}</span>
                </motion.button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
